import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { NumStepper } from "@/components/ui/num-stepper";

const RATE_FIELDS = [
  { k: "sheet_rate", label: "Sheet Rate (₹/kg)", testid: "rate-v2-sheet" },
  { k: "gst", label: "GST %", testid: "rate-v2-gst" },
  { k: "overhead", label: "Overhead %", testid: "rate-v2-overhead" },
  { k: "margin", label: "Margin %", testid: "rate-v2-margin" },
];

const COMPANY_FIELDS = [
  { k: "name", label: "Company Name", testid: "company-name" },
  { k: "tagline", label: "Tagline", testid: "company-tagline" },
  { k: "phones", label: "Phone(s)", testid: "company-phones" },
  { k: "email", label: "Email", testid: "company-email" },
  { k: "gstin", label: "GSTIN (yours)", testid: "company-gstin" },
  { k: "address", label: "Address", testid: "company-address" },
];

export const AdminRates = ({ client, api, canEdit = false }) => {
  const [rates, setRates] = useState({ sheet_rate: 77, gst: 18, overhead: 10, margin: 10 });
  const [terms, setTerms] = useState([]);
  const [company, setCompany] = useState({});
  const [customSizes, setCustomSizes] = useState([]);
  const [newSize, setNewSize] = useState({ label: "", f1: "", width: "", f2: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get(`${api}/qv2/rates`);
      setRates(data.rates);
      setTerms(data.terms || []);
      setCompany(data.company || {});
      setCustomSizes(data.custom_sizes || []);
    } catch {
      toast.error("Could not load rate settings");
    } finally {
      setLoading(false);
    }
  }, [client, api]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`${api}/qv2/rates`, { rates, terms, company, custom_sizes: customSizes });
      toast.success("Rate settings saved");
    } catch {
      toast.error("Could not save rates");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 size={32} className="animate-spin text-smoke" />
      </div>
    );
  }

  return (
    <div data-testid="rates-panel-v2" className="max-w-3xl">
      <p className="font-body text-sm text-smoke mb-8 max-w-xl">
        Sheet rate, overhead, margin and GST are controlled here. Only admins can change these —
        sales users cannot see or modify these values.
      </p>
      {!canEdit && (
        <p data-testid="rates-readonly-note" className="font-mono text-[10px] tracking-[0.3em] uppercase text-yellow-300 mb-6">
          View-only access — you cannot change rate settings.
        </p>
      )}

      <div className="bg-steel border border-white/10 p-6 md:p-8 mb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-5">Rates</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {RATE_FIELDS.map((r) => (
            <NumStepper
              key={r.k}
              label={r.label}
              testid={r.testid}
              step={0.5}
              value={rates[r.k]}
              onChange={(v) => setRates({ ...rates, [r.k]: Number(v) || 0 })}
            />
          ))}
        </div>
      </div>

      <div className="bg-steel border border-white/10 p-6 md:p-8 mb-6">
        <div className="flex items-center justify-between mb-5">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Terms & Conditions (printed on every quotation)</p>
          <button data-testid="term-add-button" onClick={() => setTerms([...terms, ""])} className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#7FB3E8] hover:text-white transition-colors duration-300 flex items-center gap-1">
            <Plus size={13} /> Add Term
          </button>
        </div>
        <div className="flex flex-col gap-3" data-testid="terms-list">
          {terms.map((t, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="font-mono text-xs text-smoke pt-3 w-5 shrink-0">{i + 1}.</span>
              <textarea
                data-testid={`term-input-${i}`}
                rows={1}
                value={t}
                onChange={(e) => setTerms(terms.map((x, j) => (j === i ? e.target.value : x)))}
                className="flex-1 bg-transparent border-0 border-b border-white/25 py-2.5 font-body text-sm text-white focus:border-[#7FB3E8] focus:outline-none resize-none transition-colors duration-300"
              />
              <button data-testid={`term-up-${i}`} disabled={i === 0} onClick={() => { const n = [...terms]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setTerms(n); }} aria-label="Move up" className="p-2 text-white/50 hover:text-white disabled:opacity-30 transition-colors duration-300">
                <ChevronUp size={15} />
              </button>
              <button data-testid={`term-down-${i}`} disabled={i === terms.length - 1} onClick={() => { const n = [...terms]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; setTerms(n); }} aria-label="Move down" className="p-2 text-white/50 hover:text-white disabled:opacity-30 transition-colors duration-300">
                <ChevronDown size={15} />
              </button>
              <button data-testid={`term-delete-${i}`} onClick={() => setTerms(terms.filter((_, j) => j !== i))} aria-label="Delete term" className="p-2 text-white/50 hover:text-red-400 transition-colors duration-300">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-steel border border-white/10 p-6 md:p-8 mb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-5">Company Details (on letterhead)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {COMPANY_FIELDS.map((f) => (
            <div key={f.k} className={f.k === "address" ? "md:col-span-2" : ""}>
              <label htmlFor={f.testid} className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">{f.label}</label>
              <input
                id={f.testid}
                data-testid={f.testid}
                value={company[f.k] || ""}
                onChange={(e) => setCompany({ ...company, [f.k]: e.target.value })}
                className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2.5 font-body text-base text-white focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-steel border border-white/10 p-6 md:p-8 mb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-2">Logo (PNG/JPG, replaces text badge on letterhead)</p>
        <input
          type="file"
          accept="image/png,image/jpeg"
          data-testid="logo-upload-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            const r = new FileReader();
            r.onload = () => setCompany({ ...company, logo: r.result });
            r.readAsDataURL(f);
          }}
          className="font-mono text-xs text-smoke"
        />
        {company.logo && (
          <div className="mt-3 flex items-center gap-4">
            <img src={company.logo} alt="Letterhead logo" className="h-12 bg-white p-1.5" data-testid="logo-preview" />
            <button data-testid="logo-remove-button" onClick={() => setCompany({ ...company, logo: "" })} className="font-mono text-[10px] tracking-widest uppercase text-white/50 hover:text-red-400 transition-colors duration-300">Remove</button>
          </div>
        )}
      </div>

      <div className="bg-steel border border-white/10 p-6 md:p-8 mb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-2">Custom Tray Sizes</p>
        <p className="font-body text-xs text-smoke mb-5">These appear in the "Standard Size" dropdown on New Quotation so recurring sizes become one-click.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          <div className="col-span-2 md:col-span-1">
            <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">Label</label>
            <input data-testid="size-label-input" value={newSize.label} onChange={(e) => setNewSize({ ...newSize, label: e.target.value })} placeholder="e.g. Standard 100" className="mt-1 w-full bg-transparent border-0 border-b border-white/25 py-2 font-body text-sm text-white placeholder:text-white/30 focus:border-[#7FB3E8] focus:outline-none transition-colors duration-300" />
          </div>
          {[["f1", "F1 mm"], ["width", "W mm"], ["f2", "F2 mm"]].map(([k, lbl]) => (
            <NumStepper key={k} label={lbl} testid={`size-${k}-input`} value={newSize[k]} onChange={(v) => setNewSize({ ...newSize, [k]: v })} />
          ))}
          <div className="flex items-end">
            <button
              data-testid="size-add-button"
              onClick={() => {
                if (!newSize.f1 || !newSize.width || !newSize.f2) { toast.error("Fill F1, W and F2"); return; }
                setCustomSizes([...customSizes, { label: newSize.label || `${newSize.f1}×${newSize.width}×${newSize.f2} mm`, f1: Number(newSize.f1), width: Number(newSize.width), f2: Number(newSize.f2) }]);
                setNewSize({ label: "", f1: "", width: "", f2: "" });
              }}
              className="btn-sweep btn-ghost font-heading font-semibold uppercase tracking-wider text-sm px-4 py-2 flex items-center gap-1"
            >
              <Plus size={14} /> Add Size
            </button>
          </div>
        </div>
        {customSizes.length === 0 ? (
          <p className="font-mono text-xs text-smoke" data-testid="sizes-empty">No custom sizes yet.</p>
        ) : (
          <div className="border-t border-white/10" data-testid="sizes-list">
            {customSizes.map((s, i) => (
              <div key={i} className="py-2.5 border-b border-white/10 flex items-center justify-between">
                <span className="font-mono text-xs text-white">{s.label} <span className="text-smoke">— {s.f1}×{s.width}×{s.f2} mm</span></span>
                <button data-testid={`size-delete-${i}`} onClick={() => setCustomSizes(customSizes.filter((_, j) => j !== i))} aria-label="Delete size" className="p-1.5 text-white/50 hover:text-red-400 transition-colors duration-300"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {canEdit && (
        <button data-testid="rates-v2-save-button" onClick={save} disabled={saving} className="btn-sweep btn-solid font-heading font-semibold uppercase tracking-wider text-lg px-8 py-3 flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save Rate Settings
        </button>
      )}
    </div>
  );
};
